import { Directive, Input, ElementRef, Renderer2, ViewContainerRef, ComponentRef, OnInit, OnDestroy, Optional, Self, HostListener } from '@angular/core';
import { NgControl, AbstractControl } from '@angular/forms';
import { NbIconComponent } from '@nebular/theme';
import { Subject } from 'rxjs';
import { takeUntil, startWith } from 'rxjs/operators';

type IndicatorStatus = 'valid' | 'invalid' | 'pending';

export const DEFAULT_STATUS_ICONS: Record<IndicatorStatus, string> = {
  valid: 'checkmark-circle-2-outline',
  invalid: 'alert-circle-outline',
  pending: 'loader-outline',
};

type StatusIconConfig = {
    showOnControlStatus?: IndicatorStatus[];
    invalidRequiresTouched?: boolean;
    invalidRequiresDirty?: boolean;
    statusIcons?: Record<IndicatorStatus, string>;
    validRequiresTouched?: boolean;
    validRequiresDirty?: boolean;
    invalidOnParentErrors?: string[];   /**
   * Parent FormGroup error keys that should also force THIS control to the
   * 'invalid' icon — for cross-field validators whose error lives on the group.
   *   [invalidOnParentErrors]="['passwordMatchValidator']"
   */
    invalidWhen?: 'touched' | 'dirty' | 'always' | 'touchedOrDirty';
  }
  
@Directive({
  selector: 'input[showStatusIconOn], textarea[showStatusIconOn], nb-select[showStatusIconOn]',
  standalone: true,
})
export class StatusIconDirective implements OnInit, OnDestroy {

  @Input() showStatusIconOn: StatusIconConfig = {};
  private config!: Required<StatusIconConfig>;

  private wrapper?: HTMLElement;
  private iconRef?: ComponentRef<NbIconComponent>;
  private destroy$ = new Subject<void>();
  private lastStatus?: IndicatorStatus;
  private resizeObserver?: ResizeObserver;

  constructor(private host: ElementRef<HTMLElement>, private renderer: Renderer2, private vcr: ViewContainerRef, @Optional() @Self() private ngControl: NgControl) {}

  ngOnInit(): void {
    this.config = {
      showOnControlStatus: this.showStatusIconOn.showOnControlStatus ?? ['valid', 'invalid', 'pending'],
      invalidRequiresTouched: this.showStatusIconOn.invalidRequiresTouched ?? false,
      invalidRequiresDirty: this.showStatusIconOn.invalidRequiresDirty ?? false,
      statusIcons: this.showStatusIconOn.statusIcons ?? { ...DEFAULT_STATUS_ICONS },
      validRequiresTouched: this.showStatusIconOn.validRequiresTouched ?? false,
      validRequiresDirty: this.showStatusIconOn.validRequiresDirty ?? false,
      invalidOnParentErrors: this.showStatusIconOn.invalidOnParentErrors ?? [],
      invalidWhen: this.showStatusIconOn.invalidWhen ?? 'touchedOrDirty',
    };

    this.wrapInput();

    // Re-align when the row's layout shifts (e.g. error text or sibling
    // buttons appearing/disappearing changes the input's position).
    this.resizeObserver = new ResizeObserver(() => this.positionIcon());
    this.resizeObserver.observe(this.wrapper!);

    const control = this.ngControl?.control;
    if (!control) return;

    control.statusChanges
      .pipe(startWith(control.status), takeUntil(this.destroy$))
      .subscribe(() => this.render(control));

    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.render(control));

    // react to cross-field errors sitting on the parent group.
    // valueChanges (not statusChanges) because a cross-field result can flip
    // while the group's overall VALID/INVALID status stays unchanged.
    if (control.parent && this.config.invalidOnParentErrors.length) {
      control.parent.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.render(control));
    }
  }

  @HostListener('blur')
  onBlur(): void {
    const control = this.ngControl?.control;
    if (!control) return;
    Promise.resolve().then(() => this.render(control));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.iconRef?.destroy();
    this.resizeObserver?.disconnect();
  }

  private currentStatus(control: AbstractControl): IndicatorStatus | null {
    // A disabled control (e.g. the read-only Role field) is neither valid
    // nor invalid — status is DISABLED. Never show a status icon for it.
    if (control.disabled) return null;

    if (control.pending) return 'pending';

    const parentGroup = control.parent;
    const parentFlagged =
      !!parentGroup &&
      this.config.invalidOnParentErrors.some(
        (key) => parentGroup.errors?.[key] || parentGroup.errors?.['failedValidator'] === key,
      );

    const interacted =
      this.config.invalidWhen === 'always' ||
      (this.config.invalidWhen === 'touched' && control.touched) ||
      (this.config.invalidWhen === 'dirty' && control.dirty) ||
      (this.config.invalidWhen === 'touchedOrDirty' && (control.touched || control.dirty));

    const isInvalid = !control.valid || parentFlagged;

    if (isInvalid) {
      // Pristine + invalid mirrors the "warning" (yellow) status used across
      // the forms for fields not yet filled in — show the pending icon
      // instead of the invalid one until the user actually interacts.
      if (!interacted) {
        return control.pristine ? 'pending' : null;
      }
      return 'invalid';
    }
    if (!control.validator && !control.asyncValidator && !control.touched && !control.dirty) {
      return null;
    }

    return 'valid';
  }

  private render(control: AbstractControl): void {
    const status = this.currentStatus(control);

    if (status === 'valid' && this.config.validRequiresTouched && !control.touched) {
      this.setVisible(false);
      this.lastStatus = undefined;
      return;
    }

    if (status === 'valid' && this.config.validRequiresDirty && !control.dirty) {
      this.setVisible(false);
      this.lastStatus = undefined;
      return;
    }

    if(status == 'invalid' && this.config.invalidRequiresTouched && !control.touched) {
      this.setVisible(false);
      this.lastStatus = undefined;
      return;
    }

    if (status == 'invalid' && this.config.invalidRequiresDirty && !control.dirty) {
      this.setVisible(false);
      this.lastStatus = undefined;
      return;
    }

    if (!status || !this.config.showOnControlStatus.includes(status)) {
      this.setVisible(false);
      this.lastStatus = undefined;
      return;
    }
    if (this.lastStatus !== status) {
      this.iconRef?.destroy();
      this.iconRef = this.vcr.createComponent(NbIconComponent);
      const el = this.iconRef.location.nativeElement as HTMLElement;
      this.renderer.addClass(el, 'status-indicator-icon');
      this.renderer.appendChild(this.wrapper, el);

      this.iconRef.instance.icon = this.config.statusIcons[status];
      this.iconRef.instance.pack = 'eva';
      el.dataset['status'] = status;
      this.iconRef.changeDetectorRef.detectChanges();

      this.lastStatus = status;
    }

    this.setVisible(true);
    this.positionIcon();
  }


  private positionIcon(): void {
    if (!this.iconRef || !this.wrapper) return;
    const input = this.host.nativeElement;
    const el = this.iconRef.location.nativeElement as HTMLElement;

    const inputRect = input.getBoundingClientRect();
    const wrapperRect = this.wrapper.getBoundingClientRect();

    const top = inputRect.top - wrapperRect.top + inputRect.height / 2;
    const right = wrapperRect.right - inputRect.right + (
      this.wrapper.classList.contains('status-indicator-wrapper--select') ? 2.2 * 16 : 9.6
    );

    this.renderer.setStyle(el, 'top', `${top}px`);
    this.renderer.setStyle(el, 'right', `${right}px`);
  }

  private setVisible(visible: boolean): void {
    if (!this.iconRef) return;
    this.renderer.setStyle(
      this.iconRef.location.nativeElement,
      'display',
      visible ? 'flex' : 'none',
    );
  }

  private wrapInput(): void {
    const input = this.host.nativeElement;
    const parent = this.renderer.parentNode(input) as HTMLElement;

    this.wrapper = parent;
    this.renderer.addClass(this.wrapper, 'status-indicator-wrapper');

    if (input.tagName.toLowerCase() === 'nb-select') {
      this.renderer.addClass(this.wrapper, 'status-indicator-wrapper--select');
    } else {
      this.renderer.setStyle(input, 'padding-right', '2rem');
    }
  }
}